import React, { useState, useEffect } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { IScheduledReport } from './scheduled-reports'
import Creatable from 'react-select/creatable'
import { IFilterSelectorOpts, IFilterSelectorOpts_Selected, PublisherOrCampaign } from '../home/dashboard-content/types'
import FiltersGrid from "../_filters-grid/filters-grid"
import { ICustomAlert } from '../alerts/custom-alerts/custom-alerts'
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { useAuth } from '../../../auth/auth-context'

type IScheduledReportModalProps = {
    show: boolean
    onClose: () => void
    onSave: (report: IScheduledReport) => Promise<void>
    onDelete: (reportId: string) => Promise<void>
    campaigns: PublisherOrCampaign[]
    publishers: PublisherOrCampaign[]
    channels: PublisherOrCampaign[]
    networks: string[]
    countries: string[]
    report?: IScheduledReport
    alerts: ICustomAlert[]
}

const isValidEmail = (email: string) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailPattern.test(email)
}

function isSameItem(a: any, b: any): boolean {
    if (typeof a === 'string' && typeof b === 'string') return a === b
    return a.id === b.id && a.network === b.network && a.country === b.country
}

function isAllSelected<T>(
    selected: T[],
    all: T[],
    compareFn: (a: T, b: T) => boolean
): boolean {
    if (selected.length !== all.length) return false
    return all.every(a => selected.some(b => compareFn(a, b)))
}

const ScheduledReportModal: React.FC<IScheduledReportModalProps> = ({ show, onClose, onSave, onDelete, report, campaigns, publishers, networks, countries, alerts, channels }) => {
    const {user} = useAuth()
    const feedAlerts = user?.kind === 'publisher' && (user as any)?.feed?.disabled ? [] : [{id:'feed', name: 'Feed alerts'}]
    const allAlerts:{id:string, name:string}[] = user?.kind === 'publisher'
        ? [...feedAlerts, ...alerts]
        : [{id:'duplicate', name: 'Duplicate conversions'}, {id:'feed', name: 'Feed alerts'}, ...alerts] 
    const [localReport, setLocalReport] = useState<IScheduledReport>({
        id: '',
        name: '',
        type: 'weekly',
        emails: [],
        campaigns: [],
        countries: [],
        networks: [],
        publishers: [],
        channels: [],
        includePerformanceData: false,
        includeMonthlyPerformance: false,
        includeKeyMetrics: false,
        includeAlerts: false,
        alertIds: []
    })
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [emailError, setEmailError] = useState<string | null>(null)
    const [showPerformanceData, setShowPerformanceData] = useState(false)
    const [includeMonthlyPerformance, setIncludeMonthlyPerformance] = useState(false)
    const [includeKeyMetrics, setIncludeKeyMetrics] = useState(false)
    const [showAlerts, setShowAlerts] = useState(false)
    const [selectedAlerts, setSelectedAlerts] = useState<string[]>([]) 
    const [emailBody, setEmailBody] = useState("")
    const [emailSubject, setEmailSubject] = useState("")
    const [showEmail, setShowEmail] = useState(false)
    
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    
    const [filters, setFilters] = useState<IFilterSelectorOpts>({
        countries: countries || [],
        networks: networks || [],
        publishers: publishers || [],
        campaigns: campaigns || [],
        channels: channels || []
    })

    function areAllItemsSelected(current: any[], all: any[]) {
        return current.length === all.length &&
            all.every(item =>
                current.some(i => isSameItem(i, item))
            )
    }

    const handleSelectAll = (
        filterKey: keyof typeof filters,
        options: any[]
    ) => {
        setFilters((prev) => ({
            ...prev,
            [filterKey]: areAllItemsSelected(prev[filterKey], options)
                ? []
                : [...options],
        }))
    }
    

    useEffect(() => {
        if (report) {
            setLocalReport(report)

            setShowPerformanceData(report.includePerformanceData !== false)
            setIncludeMonthlyPerformance(report.includeMonthlyPerformance === true)
            setIncludeKeyMetrics(report.includeKeyMetrics === true)
            setShowAlerts(report.includeAlerts === true)
            setSelectedAlerts(report.alertIds || [])
            setShowEmail(report.useCustomEmail === true)
            setEmailSubject(report.customEmailSubject || '')
            setEmailBody(report.customEmailBody || '')

            const pubs = report.publishers === true || report.publishers === undefined ? publishers : report.publishers
            const nets = report.networks === true || report.networks === undefined ? networks : report.networks
            const chans = report.channels === true || report.channels === undefined ? channels : report.channels
            
            const counts = report.countries === true || report.countries === undefined ? countries : report.countries
            const camps = report.campaigns === true || report.campaigns === undefined ? campaigns 
            : typeof report.campaigns[0] !== 'string' ? report.campaigns as PublisherOrCampaign[]
            : campaigns.filter(c => (report.campaigns as string[]).includes(`${c.id}__${c.country}__${c.network}`))
            setFilters({campaigns:camps,countries:counts,networks:nets,publishers:pubs, channels: chans})
        } else {
            setLocalReport({
                id: '',
                name: '',
                type: 'weekly',
                emails: [],
                campaigns: [],
                countries: [],
                networks: [],
                publishers: [],
                channels: [],
                includePerformanceData: false,
                includeMonthlyPerformance: false,
                includeKeyMetrics: false,
                includeAlerts: false,
                alertIds: allAlerts.map(a => a.id)
            })

            setShowPerformanceData(false)
            setIncludeMonthlyPerformance(false)
            setIncludeKeyMetrics(false)
            setShowAlerts(false)
            setSelectedAlerts(allAlerts.map(a => a.id))
            setShowEmail(false)
            setEmailSubject('')
            setEmailBody('')

            setFilters({campaigns,countries,networks,publishers, channels})
        }
    }, [report, show, campaigns])


    const handleSave = async () => {
        if (!showPerformanceData && !showAlerts) {
            alert('Please select at least one of "Performance data" or "Alerts" to include in the report.')
            return
        }
        if(showAlerts && selectedAlerts.length === 0) {
            alert('Please select at least one alert or deselect "Alerts" completely.')
            return
        }
        if (localReport.name && localReport.emails.length > 0) {
            if (emailError) {
                alert('Please fix the email errors before saving.')
                return
            }
    
            setIsSaving(true)
            try {
                const allCountriesSelected = isAllSelected(filters.countries, countries, (a, b) => a === b)
                const allNetworksSelected = isAllSelected(filters.networks, networks, (a, b) => a === b)
                const allCampaignsSelected = isAllSelected(filters.campaigns, campaigns, isSameItem)
                const allPublishersSelected = isAllSelected(filters.publishers, publishers, isSameItem)
                const allChannelsSelected = isAllSelected(filters.channels, channels, isSameItem)
        
                const newFilters: IFilterSelectorOpts_Selected = {
                    campaigns: allCampaignsSelected ? true : filters.campaigns,
                    countries: allCountriesSelected ? true : filters.countries,
                    networks: allNetworksSelected ? true : filters.networks,
                    publishers: allPublishersSelected ? true : filters.publishers,
                    channels: allChannelsSelected ? true : filters.channels
                }

                await onSave({ 
                    ...localReport, 
                    campaigns: newFilters.campaigns, 
                    countries: newFilters.countries, 
                    networks: newFilters.networks, 
                    publishers: newFilters.publishers,
                    channels: newFilters.channels,
                    includePerformanceData: showPerformanceData,
                    includeMonthlyPerformance,
                    includeKeyMetrics,
                    includeAlerts: showAlerts,
                    alertIds: selectedAlerts,
                    useCustomEmail: showEmail,
                    customEmailSubject: emailSubject,
                    customEmailBody: emailBody
                 })
                onClose();
            } finally {
                setIsSaving(false)
            }
        } else {
            alert('Please provide a name and at least one recipient email')
        }
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        handleSave()
    }

    const handleEmailChange = (selectedEmails: any) => {
        const emails: string[] = selectedEmails ? selectedEmails.map((email: { value: string }) => email.value) : []
        const invalidEmails = emails.filter((email) => !isValidEmail(email))

        setEmailError(invalidEmails.length > 0 ? 'Some emails are invalid. Please check them.' : null)
        setLocalReport({
            ...localReport,
            emails
        })
    }

    const handleCreateEmail = (inputValue: string) => {
        if (isValidEmail(inputValue)) {
            setLocalReport({
                ...localReport,
                emails: [...localReport.emails, inputValue]
            })
            setEmailError(null)
        } else {
            setEmailError('Invalid email address format.')
        }
    }

    const handleDelete = async () => {
        if (onDelete && report?.id) {
            setIsDeleting(true)
            setShowDeleteConfirm(false)

            try {
                await onDelete(report.id)
                onClose()
            } finally {
                setIsDeleting(false)
            }
        }
    }

    return (
        <>
            <Modal show={show} onHide={onClose} size="lg" backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>{report ? 'Edit Scheduled Report' : 'Create Scheduled Report'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group controlId="name">
                            <Form.Label>Report title</Form.Label>
                            <Form.Control
                                type="text"
                                value={localReport.name}
                                onChange={(e) => setLocalReport({ ...localReport, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group controlId="emails" className="mt-3">
                            <Form.Label>Recipients (Emails)</Form.Label>
                            <Creatable
                                isMulti
                                name="emails"
                                options={localReport.emails.map(email => ({ value: email, label: email }))}
                                value={localReport.emails.map(email => ({ value: email, label: email }))}
                                onChange={handleEmailChange}
                                placeholder="Type email addresses"
                                closeMenuOnSelect={true}
                                onCreateOption={handleCreateEmail}
                                getOptionLabel={(e) => e.label}
                                formatCreateLabel={(inputValue) => 'Add "'+ inputValue+'"'}
                                noOptionsMessage={() => ("Start typing an email...")}
                            />
                            {emailError && <div className="text-danger mt-2">{emailError}</div>}
                        </Form.Group>

                        <Form.Group controlId="type" className="mt-3">
                            <Form.Label>Frequency</Form.Label>
                            <Form.Control
                                as="select"
                                value={localReport.type}
                                onChange={(e) => setLocalReport({ ...localReport, type: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                            >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </Form.Control>
                        </Form.Group>
                        <div className="mt-4 border rounded p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div>
                                    <h5 className="mb-0">Performance data</h5>
                                    <small className="text-muted">
                                        {showPerformanceData ? "Included in report" : "Not included in report"}
                                    </small>
                                </div>
                                <Form.Check 
                                    type="switch"
                                    id="performance-data-switch"
                                    label=""
                                    checked={showPerformanceData}
                                    onChange={(e) => {
                                        const checked = e.target.checked
                                        setShowPerformanceData(checked)
                                        setLocalReport(prev => ({ ...prev, includePerformanceData: checked }))
                                    }}
                                />
                            </div>

                            {showPerformanceData && (
                                <>
                                    <Form.Group className="mt-2">
                                        <Form.Label>Performance sections</Form.Label>
                                        <Form.Check
                                            type="checkbox"
                                            id="scheduled-monthly-performance"
                                            label="Monthly performance"
                                            checked={includeMonthlyPerformance}
                                            onChange={(e) => setIncludeMonthlyPerformance(e.target.checked)}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="scheduled-key-metrics"
                                            label="Key metrics - Last two months"
                                            checked={includeKeyMetrics}
                                            onChange={(e) => setIncludeKeyMetrics(e.target.checked)}
                                        />
                                    </Form.Group>
                                    <div className="mt-3 pt-3 border-top">
                                        <Form.Label>Data selection</Form.Label>
                                    </div>
                                    <FiltersGrid
                                        opts={{ campaigns, countries, networks, publishers, channels }}
                                        selectedFilters={filters}
                                        allCollapsed
                                        onItemSelect={(type, item) => {
                                            setFilters((prev) => {
                                                const current = prev[type]
                                                const isSelected =
                                                    typeof item === 'string'
                                                        ? (current as string[]).includes(item)
                                                        : (current as PublisherOrCampaign[]).some(
                                                            (i) =>
                                                                i.id === item.id &&
                                                                i.network === item.network &&
                                                                i.country === item.country
                                                        )

                                                const updated = isSelected
                                                    ? (current as any[]).filter((i) =>
                                                        typeof item === 'string'
                                                            ? i !== item
                                                            : i.id !== item.id || i.network !== item.network || i.country !== item.country
                                                    )
                                                    : [...current, item]

                                                return {
                                                    ...prev,
                                                    [type]: updated,
                                                }
                                            })
                                        }}
                                        onSelectAll={handleSelectAll}
                                    />
                                </>
                            )}
                        </div>
                        <div className="mt-4 border rounded p-3">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div>
                                        <h5 className="mb-0">Alerts</h5>
                                        <small className="text-muted">
                                            {showAlerts ? "Included in report" : "Not included in report"}
                                        </small>
                                    </div>
                                    <Form.Check 
                                        type="switch"
                                        id="alerts-switch"
                                        label=""
                                        checked={showAlerts}
                                        onChange={(e) => {
                                            const checked = e.target.checked
                                            setShowAlerts(checked)
                                            setLocalReport(prev => ({ ...prev, includeAlerts: checked }))
                                        }}
                                    />
                                </div>

                                {showAlerts && (
                                    <>
                                        <div className="mt-2">
                                            <div className="row">
                                                {allAlerts.map(alert => (
                                                    <div className="col-6" key={alert.id}>
                                                        <Form.Check
                                                            type="checkbox"
                                                            id={`alert-${alert.id}`}
                                                            label={alert.name}
                                                            checked={selectedAlerts.includes(alert.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedAlerts(prev => [...prev, alert.id])
                                                                } else {
                                                                    setSelectedAlerts(prev => prev.filter(id => id !== alert.id))
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        {!showPerformanceData && (
                                            <div className="alert alert-light mt-2">
                                                Emails will only be sent if new alerts occur during the chosen frequency period.
                                            </div>
                                        )}
                                    </>
                                )}

                        </div>
                        <div className="mt-4 border rounded p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div>
                                <h5 className="mb-0">Custom Email</h5>
                                <small className="text-muted">
                                    {showEmail
                                    ? "A custom email will be sent with this report"
                                    : "Default email template will be used"}
                                </small>
                                </div>
                                <Form.Check
                                type="switch"
                                id="custom-email-switch"
                                label=""
                                checked={showEmail || false}
                                onChange={(e) => {
                                    setShowEmail(e.target.checked)
                                }}
                                />
                            </div>

                            {showEmail && (
                                <>
                                    <Form.Group controlId="customEmailSubject" className="mt-2">
                                        <Form.Label>Email Subject</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter email subject"
                                            value={emailSubject || ""}
                                            onChange={(e) => setEmailSubject(e.target.value)}
                                        />
                                    </Form.Group>

                                    <Form.Group controlId="customEmailBody" className="mt-3">
                                        <Form.Label>Email Body</Form.Label>
                                            <div>
                                                <SunEditor
                                                    setContents={emailBody}
                                                    onChange={(html) => setEmailBody(html)}
                                                    setOptions={{
                                                        buttonList: [
                                                            ["bold", "italic", "underline"],
                                                            ["fontSize", "align", "list"],
                                                            ["link", "removeFormat"],
                                                        ],
                                                        defaultTag: "div",
                                                        fontSize: [10, 12, 14, 16, 18, 24, 32],
                                                        minHeight: "200px",
                                                        showPathLabel: false,
                                                        linkProtocol: "https://",
                                                    }}
                                                />
                                            </div>
                                        <Form.Text className="text-muted">
                                            You can insert <code>[From Date]</code> and <code>[To Date]</code> in either the subject or body.  
                                            These will be automatically replaced with the actual report dates when the email is sent.  
                                            Eg. <i>This report is from [From Date] to [To Date].</i>
                                        </Form.Text>
                                    </Form.Group>
                                </>
                            )}
                            </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    {report && (
                    <Button className='me-auto' variant="danger" onClick={() => setShowDeleteConfirm(true)} disabled={isSaving || isDeleting}>
                        {isDeleting ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : 'Delete'}
                    </Button>
                    )}
                    <Button variant="secondary" onClick={onClose} disabled={isSaving || isDeleting}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={isSaving || isDeleting}>
                        {isSaving ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this scheduled report?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default ScheduledReportModal
